import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getAttendance = createAsyncThunk(
    'attendance/getAttendance',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.ATTENDANCE, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Attendance records retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch attendance records',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch attendance records';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch attendance records';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getAttendanceStats = createAsyncThunk(
    'attendance/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.ATTENDANCE_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Attendance statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch attendance statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch attendance statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch attendance statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getAtRiskMembers = createAsyncThunk(
    'attendance/getAtRisk',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.ATTENDANCE_AT_RISK, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'At-risk members retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch at-risk members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch at-risk members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch at-risk members';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getMemberAttendanceHistory = createAsyncThunk(
    'attendance/getMemberHistory',
    async ({ memberId, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.ATTENDANCE_MEMBER_HISTORY(memberId), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member attendance history retrieved successfully',
                    history: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch member attendance history',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch member attendance history';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch member attendance history';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const checkInMember = createAsyncThunk(
    'attendance/checkIn',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.ATTENDANCE_CHECK_IN, data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Member checked in successfully',
                    attendance: responseData?.attendance || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to check in member',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to check in member';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to check in member';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const bulkCheckInMembers = createAsyncThunk(
    'attendance/bulkCheckIn',
    async (data, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.ATTENDANCE_BULK_CHECK_IN, data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Bulk check-in completed',
                    result: responseData || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to bulk check in members',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to bulk check in members';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to bulk check in members';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const closeCheckInWindow = createAsyncThunk(
    'attendance/closeWindow',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.ATTENDANCE_CLOSE_WINDOW, params);
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Check-in window closed successfully',
                    result: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to close check-in window',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to close check-in window';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to close check-in window';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportAttendance = createAsyncThunk(
    'attendance/export',
    async ({ type, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.ATTENDANCE_EXPORT(type), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Export data generated successfully',
                    exportData: data || {},
                    type: type
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to export data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to export data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
    records: [],
    currentRecord: null,
    atRiskMembers: [],
    memberHistory: null,
    stats: {
        last_sunday_attendance: 0,
        month_average: 0,
        sms_check_ins: 0,
        at_risk_count: 0,
        total_members: 0,
        weekly_data: [],
        attendance_rate: 0,
        stats: {
            last_sunday: 0,
            month_avg: 0,
            sms_checkins: 0,
            at_risk: 0,
            total_members: 0,
            attendance_rate: 0
        }
    },
    exportData: null,
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

// ============================================
// SLICE
// ============================================

const attendanceSlice = createSlice({
    name: 'attendance',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearAttendanceState: (state) => {
            state.records = [];
            state.currentRecord = null;
            state.atRiskMembers = [];
            state.memberHistory = null;
            state.stats = initialState.stats;
            state.exportData = null;
            state.pagination = initialState.pagination;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentRecord: (state) => {
            state.currentRecord = null;
        },
        clearAtRiskMembers: (state) => {
            state.atRiskMembers = [];
        },
        clearExportData: (state) => {
            state.exportData = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET ATTENDANCE =====
            .addCase(getAttendance.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getAttendance.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.records = responseData.data || [];
                state.pagination = responseData.meta || initialState.pagination;
                if (responseData.meta?.stats) {
                    state.stats = { ...state.stats, ...responseData.meta.stats };
                }
                state.successMessage = payload.message || 'Attendance records retrieved successfully';
                state.apiMessage = payload.message || 'Attendance records retrieved successfully';
                state.error = null;
            })
            .addCase(getAttendance.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch attendance records';
                state.apiMessage = action.payload?.message || 'Failed to fetch attendance records';
                state.successMessage = null;
                state.records = [];
                state.pagination = initialState.pagination;
            })

            // ===== GET STATS =====
            .addCase(getAttendanceStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getAttendanceStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = { ...state.stats, ...(payload.stats || {}) };
                state.successMessage = payload.message || 'Attendance statistics retrieved successfully';
                state.apiMessage = payload.message || 'Attendance statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getAttendanceStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch attendance statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch attendance statistics';
                state.successMessage = null;
            })

            // ===== GET AT-RISK MEMBERS =====
            .addCase(getAtRiskMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getAtRiskMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.atRiskMembers = responseData.data || [];
                state.pagination = responseData.meta || initialState.pagination;
                state.successMessage = payload.message || 'At-risk members retrieved successfully';
                state.apiMessage = payload.message || 'At-risk members retrieved successfully';
                state.error = null;
            })
            .addCase(getAtRiskMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch at-risk members';
                state.apiMessage = action.payload?.message || 'Failed to fetch at-risk members';
                state.successMessage = null;
                state.atRiskMembers = [];
                state.pagination = initialState.pagination;
            })

            // ===== GET MEMBER HISTORY =====
            .addCase(getMemberAttendanceHistory.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getMemberAttendanceHistory.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.memberHistory = payload.history || null;
                state.successMessage = payload.message || 'Member attendance history retrieved successfully';
                state.apiMessage = payload.message || 'Member attendance history retrieved successfully';
                state.error = null;
            })
            .addCase(getMemberAttendanceHistory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch member attendance history';
                state.apiMessage = action.payload?.message || 'Failed to fetch member attendance history';
                state.successMessage = null;
                state.memberHistory = null;
            })

            // ===== CHECK IN =====
            .addCase(checkInMember.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(checkInMember.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Member checked in successfully';
                state.apiMessage = payload.message || 'Member checked in successfully';
                state.error = null;
                if (payload.attendance) {
                    state.records = [payload.attendance, ...state.records];
                }
            })
            .addCase(checkInMember.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to check in member';
                state.apiMessage = action.payload?.message || 'Failed to check in member';
                state.successMessage = null;
            })

            // ===== BULK CHECK IN =====
            .addCase(bulkCheckInMembers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(bulkCheckInMembers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Bulk check-in completed';
                state.apiMessage = payload.message || 'Bulk check-in completed';
                state.error = null;
            })
            .addCase(bulkCheckInMembers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to bulk check in members';
                state.apiMessage = action.payload?.message || 'Failed to bulk check in members';
                state.successMessage = null;
            })

            // ===== CLOSE WINDOW =====
            .addCase(closeCheckInWindow.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(closeCheckInWindow.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Check-in window closed successfully';
                state.apiMessage = payload.message || 'Check-in window closed successfully';
                state.error = null;
            })
            .addCase(closeCheckInWindow.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to close check-in window';
                state.apiMessage = action.payload?.message || 'Failed to close check-in window';
                state.successMessage = null;
            })

            // ===== EXPORT =====
            .addCase(exportAttendance.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportAttendance.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.exportData = payload.exportData || null;
                state.successMessage = payload.message || 'Export data generated successfully';
                state.apiMessage = payload.message || 'Export data generated successfully';
                state.error = null;
            })
            .addCase(exportAttendance.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export data';
                state.apiMessage = action.payload?.message || 'Failed to export data';
                state.successMessage = null;
                state.exportData = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearAttendanceState,
    clearCurrentRecord,
    clearAtRiskMembers,
    clearExportData,
} = attendanceSlice.actions;

export const selectAttendanceRecords = (state) => state.attendance?.records || [];
export const selectCurrentRecord = (state) => state.attendance?.currentRecord || null;
export const selectAtRiskMembers = (state) => state.attendance?.atRiskMembers || [];
export const selectMemberHistory = (state) => state.attendance?.memberHistory || null;
export const selectAttendanceStats = (state) => state.attendance?.stats || initialState.stats;
export const selectAttendancePagination = (state) => state.attendance?.pagination || initialState.pagination;
export const selectAttendanceLoading = (state) => state.attendance?.isLoading || false;
export const selectAttendanceError = (state) => state.attendance?.error || null;
export const selectAttendanceSuccess = (state) => state.attendance?.successMessage || null;
export const selectAttendanceApiMessage = (state) => state.attendance?.apiMessage || null;
export const selectAttendanceExportData = (state) => state.attendance?.exportData || null;

export const selectAttendanceStatsSummary = (state) => {
    const stats = selectAttendanceStats(state);
    return {
        lastSunday: stats.last_sunday_attendance || 0,
        monthAverage: stats.month_average || 0,
        smsCheckIns: stats.sms_check_ins || 0,
        atRiskCount: stats.at_risk_count || 0,
        totalMembers: stats.total_members || 0,
        attendanceRate: stats.attendance_rate || 0,
        weeklyData: stats.weekly_data || [],
        stats: stats.stats || {}
    };
};

export const selectAtRiskCount = (state) => {
    const stats = selectAttendanceStats(state);
    return stats.at_risk_count || 0;
};

export default attendanceSlice.reducer;